import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDbUser } from '../hooks/useDbUser';
import { db, Goal, GoalTemplate, Matter, Project, Client } from '../lib/db';
import GoalList from '../components/goals/GoalList';
import { TemplateModal } from '../components/templates/TemplateModal';
import { GoalTemplateEditModal } from '../components/goals/GoalTemplateEditModal';
import { toast } from 'sonner';

function DashboardPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const location = useLocation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [goals, setGoals] = useState<Goal[]>([]);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<{
    template: GoalTemplate;
    matterId?: string;
    projectId?: string;
  } | null>(null);
  
  // Extract highlighting info from navigation state
  const highlightGoalId = location.state?.goalId;
  const highlightTaskId = location.state?.taskId;
  const shouldHighlightTask = location.state?.highlightTask;

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadGoals();
      loadMatters();
      loadProjects();
      loadClients();
    }
  }, [dbUserId, dbUserLoading, selectedYear, showArchived]);
  
  // Clear navigation state after using it
  useEffect(() => {
    if (location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);



  const loadGoals = async () => {
    if (!dbUserId) return;
    
    try {
      let goalsData;
      if (showArchived) {
        goalsData = await db.goals.findArchivedByUserId(dbUserId);
      } else {
        goalsData = await db.goals.findByUserId(dbUserId, false);
      }
      // Ensure goalsData is an array before filtering
      const yearGoals = Array.isArray(goalsData) 
        ? goalsData.filter(goal => goal.year === selectedYear)
        : [];
      setGoals(yearGoals);
    } catch (error) {
      console.error('Error loading goals:', error);
      setGoals([]); // Set empty array on error
    }
  };

  const loadMatters = async () => {
    if (!dbUserId) return;
    
    try {
      const mattersData = await db.matters.findByUserId(dbUserId);
      setMatters(mattersData);
    } catch (error) {
      console.error('Error loading matters:', error);
      setMatters([]);
    }
  };

  const loadProjects = async () => {
    if (!dbUserId) return;
    
    try {
      const projectsData = await db.projects.findByUserId(dbUserId);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const loadClients = async () => {
    if (!dbUserId) return;
    
    try {
      const clientsData = await db.clients.findByUserId(dbUserId);
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const handleGoalCreate = async (goal: Partial<Goal>) => {
    if (!dbUserId) return;
    
    try {
      const newGoal = await db.goals.create({
        ...goal,
        user_id: dbUserId,
        year: selectedYear,
        title: goal.title || '',
        priority: goal.priority || 0,
        position: goals.length,
      } as Omit<Goal, 'id' | 'created_at' | 'updated_at'>);
      
      setGoals([...goals, newGoal]);
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleGoalUpdate = async (id: string, updates: Partial<Goal>) => {
    if (!dbUserId) return;
    
    try {
      const updatedGoal = await db.goals.update(id, dbUserId, updates);
      setGoals(goals.map(goal => goal.id === id ? updatedGoal : goal));
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const handleGoalDelete = async (id: string) => {
    if (!dbUserId) return;
    
    try {
      // Optimistically update the UI by removing the goal immediately
      setGoals(prevGoals => prevGoals.filter(goal => goal.id !== id));
      
      const success = await db.goals.delete(id, dbUserId);
      if (success) {
        toast.success('Deliverable deleted permanently');
      } else {
        // If deletion failed, reload to restore the correct state
        await loadGoals();
        toast.error('Failed to delete deliverable');
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      // Reload on error to ensure UI is in sync
      await loadGoals();
      toast.error('Failed to delete deliverable');
    }
  };

  const handleGoalReorder = async (startIndex: number, endIndex: number) => {
    if (!dbUserId) return;
    
    try {
      // Create a new array with the reordered goals
      const newGoals = Array.from(goals);
      const [removed] = newGoals.splice(startIndex, 1);
      newGoals.splice(endIndex, 0, removed);

      // Update the local state immediately for a smooth UI experience
      setGoals(newGoals);

      // Update positions in the database
      for (let i = 0; i < newGoals.length; i++) {
        await db.goals.update(newGoals[i].id, dbUserId, { position: i });
      }
    } catch (error) {
      console.error('Error reordering goals:', error);
      // If there's an error, reload the goals to ensure consistency
      await loadGoals();
    }
  };

  const handleTemplateSelect = async (template: GoalTemplate, matterId?: string, projectId?: string) => {
    // Show the edit modal instead of creating immediately
    setEditingTemplate({ template, matterId, projectId });
    setShowTemplateModal(false);
  };

  const handleTemplateConfirm = async (data: {
    title: string;
    description?: string;
    category?: string;
    priority: number;
    start_date?: string;
    deadline?: string;
    matter_id?: string;
    project_id?: string;
  }) => {
    if (!dbUserId || !editingTemplate) return;
    
    try {
      // Create the goal from template with edited data
      const newGoal = await db.goals.create({
        user_id: dbUserId,
        title: data.title,
        description: data.description,
        category: data.category,
        priority: data.priority,
        year: selectedYear,
        position: goals.length,
        matter_id: data.matter_id || undefined,
        project_id: data.project_id || undefined,
        start_date: data.start_date ? new Date(data.start_date) : undefined,
        deadline: data.deadline ? new Date(data.deadline) : undefined,
      } as Omit<Goal, 'id' | 'created_at' | 'updated_at'>);
      
      // Create tasks from templates with dependencies
      await db.createTasksFromTemplateWithDependencies(newGoal.id, editingTemplate.template.id, dbUserId);
      
      // Update local state
      setGoals([...goals, newGoal]);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error creating goal from template:', error);
    }
  };

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() + i
  );

  if (dbUserLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const handleGoalArchive = async (id: string) => {
    if (!dbUserId) return;
    
    try {
      if (showArchived) {
        // Unarchive
        await db.goals.unarchive(id, dbUserId);
      } else {
        // Archive
        await db.goals.archive(id, dbUserId);
      }
      // Remove from current view
      setGoals(goals.filter(goal => goal.id !== id));
    } catch (error) {
      console.error('Error archiving/unarchiving goal:', error);
    }
  };
  
  const handleGoalMarkComplete = async (id: string) => {
    if (!dbUserId) return;
    
    try {
      const updatedGoal = await db.goals.markComplete(id, dbUserId);
      setGoals(goals.map(goal => goal.id === id ? updatedGoal : goal));
      toast.success('Deliverable marked as complete');
    } catch (error) {
      console.error('Error marking goal as complete:', error);
      toast.error('Failed to mark deliverable as complete');
    }
  };
  
  const handleGoalMarkIncomplete = async (id: string) => {
    if (!dbUserId) return;
    
    try {
      const updatedGoal = await db.goals.markIncomplete(id, dbUserId);
      setGoals(goals.map(goal => goal.id === id ? updatedGoal : goal));
      toast.success('Deliverable marked as incomplete');
    } catch (error) {
      console.error('Error marking goal as incomplete:', error);
      toast.error('Failed to mark deliverable as incomplete');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                Select Year
              </label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="block w-full md:w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
                  !showArchived
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setShowArchived(false)}
              >
                Active
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border-l-0 border ${
                  showArchived
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setShowArchived(true)}
              >
                Archived
              </button>
            </div>
          </div>
        </div>

        <GoalList
          goals={goals}
          matters={matters}
          projects={projects}
          clients={clients}
          selectedYear={selectedYear}
          onGoalCreate={handleGoalCreate}
          onGoalUpdate={handleGoalUpdate}
          onGoalReorder={handleGoalReorder}
          onGoalArchive={handleGoalArchive}
          onGoalDelete={handleGoalDelete}
          onGoalMarkComplete={handleGoalMarkComplete}
          onGoalMarkIncomplete={handleGoalMarkIncomplete}
          onTemplateSelect={() => setShowTemplateModal(true)}
          isArchiveView={showArchived}
          highlightGoalId={highlightGoalId}
          highlightTaskId={highlightTaskId}
          shouldHighlightTask={shouldHighlightTask}
        />
        
        {dbUserId && (
          <>
            <TemplateModal
              isOpen={showTemplateModal}
              onClose={() => setShowTemplateModal(false)}
              onSelectTemplate={handleTemplateSelect}
              userId={dbUserId}
              matters={matters}
              projects={projects}
            />
            <GoalTemplateEditModal
              template={editingTemplate?.template || null}
              isOpen={!!editingTemplate}
              onClose={() => setEditingTemplate(null)}
              onConfirm={handleTemplateConfirm}
              matters={matters}
              projects={projects}
              defaultMatterId={editingTemplate?.matterId}
              defaultProjectId={editingTemplate?.projectId}
            />
          </>
        )}
    </div>
  );
}

export default DashboardPage;